import type { Maybe } from '@@types/helpers'
import type AppRouter from './AppRouter'
import type { IRouteDefinition } from './types'

/**
 * Remove prefix from the endpoint.
 */
export function removePrefixFromPathname<
  R extends string,
  RD extends IRouteDefinition<R>
>(
  appRouter: AppRouter<R, RD>,
  prefixRouteName: IRouteDefinition<R>['routeName']
) {
  return (routeName: IRouteDefinition<R>['routeName']): string => {
    const prefixEndpoint = appRouter.getRoutePathname(prefixRouteName)
    const endpoint = appRouter.getRoutePathname(routeName)
    return endpoint.replace(prefixEndpoint, '').replace(/\/+/g, '/')
  }
}

/**
 * Append prefix to the endpoint.
 */
export function appendPrefixToPathname<
  R extends string,
  RD extends IRouteDefinition<R>
>(
  appRouter: AppRouter<R, RD>,
  prefixRouteName: IRouteDefinition<R>['routeName']
) {
  return (routeName: Maybe<IRouteDefinition<R>['routeName']>): string => {
    const prefixEndpoint = appRouter.getRoutePathname(prefixRouteName)

    if (!routeName) {
      return prefixEndpoint
    }

    const endpoint = appRouter.getRoutePathname(routeName)
    return [prefixEndpoint, endpoint].join('/').replace(/\/+/g, '/')
  }
}

/**
 * Extract parameters from an url pattern.
 *
 * const pattern = '/profile/:profileName/indicators-of-exposure/ad/details/:checkerId-:checkerCodename/deviant-objects'
 * const url = '/profile/tenable/indicators-of-exposure/ad/details/20-C-PRE-WIN2000-ACCESS-MEMBERS/deviant-objects'
 *
 * Output:
 * {
 *   profileName: 'tenable',
 *   checkerId: '20',
 *   checkerCodename: 'C-PRE-WIN2000-ACCESS-MEMBERS'
 * }
 */
export function extractParamsFromUrlPattern(
  pattern: string,
  url: string
): Maybe<{ [key: string]: string }> {
  // replace the pattern by a regexp that matches every :param, each group being stopped by the `lastChar` capture
  const regexPattern = pattern.replace(
    /:([\w]+)([^\w])?/g,
    (_, paramName, lastChar) => {
      if (!lastChar) {
        // if no lastChar (because we are supposed to have reach the end of the pattern), match everything expect '/'
        // which is considered as not being part of the param
        return `(?<${paramName}>[^/]+)`
      }

      // match everything that is not `lastChar` and name it `paramName`
      return `(?<${paramName}>[^${lastChar}]+)${lastChar}`
    }
  )

  const regexp = new RegExp(`^${regexPattern}`)
  const match = url.match(regexp)

  if (match?.groups) {
    return match.groups
  }

  return null
}
